import { useEffect, useRef } from 'react';

import { useAuth } from './useAuth';

/**
 * Shared route-guard gate, mirroring the legacy portal's `useAuthGate`:
 * redirects to login (preserving the current destination) when the session
 * is involuntarily unauthenticated, and reports the coarse states guards
 * branch on. The redirect runs in an effect, not during render — a
 * render-body side effect double-fires under StrictMode.
 *
 * `signedout` and `error` deliberately do NOT auto-login (see `AuthStatus`).
 */
export function useAuthGate(): {
  isLoading: boolean;
  isUnauthed: boolean;
  isSignedOut: boolean;
  isError: boolean;
} {
  const { status, login } = useAuth();
  // Fire the redirect at most once per unauthed episode. Without this, a
  // StrictMode remount (dev) double-invokes the effect and starts login twice;
  // re-armed when the status leaves 'unauthed' so a later involuntary sign-out
  // (e.g. token expiry) still redirects.
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (status !== 'unauthed') {
      redirectStarted.current = false;
      return;
    }
    if (redirectStarted.current) return;
    redirectStarted.current = true;
    const { pathname, search } = globalThis.location;
    void login({ returnTo: `${pathname}${search}` });
  }, [status, login]);

  return {
    isLoading: status === 'loading',
    isUnauthed: status === 'unauthed' || status === 'signedout',
    isSignedOut: status === 'signedout',
    // Deliberately NOT folded into isUnauthed: a guard branching on
    // isUnauthed renders "redirecting to sign in", which is the one thing
    // that must not happen here.
    isError: status === 'error',
  };
}
